import { MY_CROPWISE_MODULE } from "constants/BasicInfo";
import { isWorkspaceId } from "shared/utils/workspaceRouting";

export const MY_CROPWISE_ROOT = `/${MY_CROPWISE_MODULE}`;

export interface DeckProductContext {
  workspaceId?: string;
  organizationId?: string;
}

export interface WorkspaceOrganization {
  id?: string;
}

const uniqueParam = (
  params: URLSearchParams,
  name: string
): string | undefined => {
  const values = params
    .getAll(name)
    .map((value) => value.trim())
    .filter((value) => value.length > 0);
  if (values.length === 0) {
    return undefined;
  }
  return new Set(values).size === 1 ? values[0] : undefined;
};

// Deck of Apps sends `workspace` and `organization`. Older links use `workspaceId`.
const workspaceFromParams = (params: URLSearchParams): string | undefined => {
  if (params.has("workspace")) {
    const workspaceId = uniqueParam(params, "workspace");
    return isWorkspaceId(workspaceId) ? workspaceId : undefined;
  }
  if (params.has("workspaceId")) {
    const workspaceId = uniqueParam(params, "workspaceId");
    return isWorkspaceId(workspaceId) ? workspaceId : undefined;
  }
  return undefined;
};

export const getDeckProductContext = (search: string): DeckProductContext => {
  const params = new URLSearchParams(search);
  return {
    workspaceId: workspaceFromParams(params),
    organizationId:
      uniqueParam(params, "organization") ?? uniqueParam(params, "orgId"),
  };
};

const isLandingPath = (pathname: string): boolean => {
  return (
    pathname === "/" ||
    pathname === "/app" ||
    pathname === "/app/" ||
    pathname === MY_CROPWISE_ROOT ||
    pathname === `${MY_CROPWISE_ROOT}/`
  );
};

// `workspaceId` on an existing /app address is legacy in-app routing, not a
// product handoff. Deck links always use `workspace` or `organization`.
export const hasIncomingProductContext = (
  pathname: string,
  search: string
): boolean => {
  const params = new URLSearchParams(search);
  if (params.has("workspace") || params.has("organization")) {
    return true;
  }
  return isLandingPath(pathname) && params.has("workspaceId");
};

const pendingEntryKey = "carbonEngine.pendingProductEntry";

interface PendingProductEntry {
  search: string;
  hash: string;
}

export const rememberProductEntry = (
  location: PendingProductEntry & { pathname: string }
): void => {
  try {
    sessionStorage.removeItem(pendingEntryKey);
    if (!hasIncomingProductContext(location.pathname, location.search)) {
      return;
    }
    const params = new URLSearchParams(location.search);
    const savedParams = new URLSearchParams();
    for (const name of ["workspace", "workspaceId", "organization", "orgId"]) {
      for (const value of params.getAll(name)) {
        savedParams.append(name, value);
      }
    }
    sessionStorage.setItem(
      pendingEntryKey,
      JSON.stringify({
        search: `?${savedParams.toString()}`,
        hash: location.hash,
      })
    );
  } catch {
    console.warn("Unable to preserve Deck context through login.");
  }
};

export const getPendingProductEntry = (): PendingProductEntry | undefined => {
  try {
    const saved: unknown = JSON.parse(
      sessionStorage.getItem(pendingEntryKey) ?? "null"
    );
    if (
      saved !== null &&
      typeof saved === "object" &&
      "search" in saved &&
      typeof saved.search === "string" &&
      "hash" in saved &&
      typeof saved.hash === "string" &&
      hasIncomingProductContext("/", saved.search)
    ) {
      return { search: saved.search, hash: saved.hash };
    }
  } catch {
    console.warn("Unable to restore Deck context after login.");
  }
  return undefined;
};

export const clearPendingProductEntry = (): void => {
  try {
    sessionStorage.removeItem(pendingEntryKey);
  } catch {
    console.warn("Unable to clear saved Deck context.");
  }
};

export const myCropwiseWorkspacePath = (workspaceId?: string): string => {
  return isWorkspaceId(workspaceId)
    ? `${MY_CROPWISE_ROOT}/workspace/${workspaceId}`
    : MY_CROPWISE_ROOT;
};

export const myCropwiseOrganizationPath = (
  workspaceId?: string,
  organizationId?: string
): string => {
  if (isWorkspaceId(workspaceId) && organizationId) {
    return [
      myCropwiseWorkspacePath(workspaceId),
      "organization",
      organizationId,
    ].join("/");
  }
  return myCropwiseWorkspacePath(workspaceId);
};

export const assessmentPath = (
  workspaceId: string,
  organizationId: string
): string => {
  return [
    "/app/workspace",
    workspaceId,
    "organization",
    organizationId,
    "assessments",
  ].join("/");
};

const organizationIds = (organizations: WorkspaceOrganization[]): string[] => {
  return organizations
    .map((organization) => organization.id)
    .filter((id): id is string => Boolean(id));
};

export const resolveAccessibleAssessmentPath = (
  context: DeckProductContext,
  organizations: WorkspaceOrganization[] | null
): string => {
  if (!isWorkspaceId(context.workspaceId) || !organizations?.length) {
    return MY_CROPWISE_ROOT;
  }
  const ids = organizationIds(organizations);
  const onlyOrganization = ids[0];
  if (!context.workspaceId || ids.length === 0 || !onlyOrganization) {
    return MY_CROPWISE_ROOT;
  }
  if (context.organizationId) {
    return ids.includes(context.organizationId)
      ? assessmentPath(context.workspaceId, context.organizationId)
      : myCropwiseWorkspacePath(context.workspaceId);
  }
  if (ids.length === 1) {
    return assessmentPath(context.workspaceId, onlyOrganization);
  }
  return myCropwiseWorkspacePath(context.workspaceId);
};
